import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';

// styles
import './BottomNav.css';

interface BottomNavLink {
  key: string;
  url: string;
  icon: React.ReactNode;
  label: string;
  permission?: string;
  /** Autres préfixes d'URL considérés comme actifs pour cet onglet. */
  activePrefixes?: string[];
  highlight?: boolean;
}

const NAV_LINKS: BottomNavLink[] = [
  {
    key: 'home',
    url: '/dashboard',
    icon: <i className='material-icons'>dashboard</i>,
    label: 'Accueil',
  },
  {
    key: 'market',
    url: '/cotations',
    icon: <i className='material-icons'>show_chart</i>,
    label: 'Marché',
    permission: 'view_market_data',
    activePrefixes: ['/cotations', '/data', '/stock'],
  },
  {
    key: 'nia',
    url: '/chat',
    icon: <Bot size={24} />,
    label: 'Nia',
    highlight: true,
  },
  {
    key: 'portfolio',
    url: '/my-assets',
    icon: <i className='material-icons'>account_balance_wallet</i>,
    label: 'Portefeuille',
    permission: 'manage_portfolio',
    activePrefixes: ['/my-assets', '/wallet', '/transactions', '/portfolio-analysis'],
  },
];

const BottomNav: React.FC = () => {
  const { hasPermission } = useAuth();
  const { isMobileMenuOpen, toggleMobileMenu } = useLayout();
  const location = useLocation();

  const isLinkActive = (link: BottomNavLink): boolean => {
    if (isMobileMenuOpen) return false;
    const prefixes = link.activePrefixes ?? [link.url];
    return prefixes.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  };

  const visibleLinks = NAV_LINKS.filter((link) => !link.permission || hasPermission(link.permission));

  return (
    <nav className='bottom-nav no-select' aria-label='Navigation principale mobile'>
      {visibleLinks.map((link) => (
        <Link
          key={link.key}
          to={link.url}
          className={`bottom-nav-item ${link.highlight ? 'bottom-nav-item-highlight' : ''} ${
            isLinkActive(link) ? 'active' : ''
          }`}
          aria-current={isLinkActive(link) ? 'page' : undefined}
        >
          <span className='bottom-nav-icon'>{link.icon}</span>
          <span className='bottom-nav-label'>{link.label}</span>
        </Link>
      ))}
      <button
        type='button'
        className={`bottom-nav-item bottom-nav-menu ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={toggleMobileMenu}
        aria-expanded={isMobileMenuOpen}
        aria-label='Ouvrir le menu complet'
      >
        <span className='bottom-nav-icon'>
          <i className='material-icons'>{isMobileMenuOpen ? 'close' : 'menu'}</i>
        </span>
        <span className='bottom-nav-label'>Menu</span>
      </button>
    </nav>
  );
};

export default BottomNav;
